import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { parseNum } from "@/lib/utils";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PersonalEntries } from "@/components/PersonalEntries";
import { IRReport } from "@/components/IRReport";
import { HandCoins, Receipt, Truck, Calendar, ChartLineUp, CurrencyDollar, Wallet } from "@phosphor-icons/react";

const fmt = (n) => (Number(n) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtNum = (n, d = 0) => (Number(n) || 0).toLocaleString("pt-BR", { minimumFractionDigits: d, maximumFractionDigits: d });

export default function MyDriverDashboard() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("month");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const years = Array.from({ length: 6 }, (_, i) => String(new Date().getFullYear() - i));

  const load = () => {
    let url = `/driver/my-dashboard?period=${period}`;
    if (period === "custom" && customStart && customEnd) url += `&start=${customStart}&end=${customEnd}`;
    api.get(url).then((r) => setData(r.data)).catch((e) => setData({ error: e?.response?.data?.detail || "Erro" }));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [period]);

  if (!data) return <p className="text-muted-foreground">Carregando…</p>;
  if (data.error) return <Card className="border-amber-300 bg-amber-50"><CardContent className="p-6 text-amber-900 font-semibold">{data.error}</CardContent></Card>;

  const t = data.totals;
  return (
    <div className="space-y-6" data-testid="my-driver-dashboard">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-900 via-blue-900 to-slate-900 p-6 md:p-8 shadow-2xl text-white">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #10b981 0%, transparent 40%)" }} />
        <div className="relative">
          <div className="flex items-center gap-2 text-emerald-300 text-xs font-bold uppercase tracking-widest">
            <HandCoins size={16} weight="fill" /> Minha área
          </div>
          <h1 className="text-3xl md:text-5xl font-black mt-1 tracking-tight" style={{ fontFamily: "Manrope" }}>Quanto eu ganhei?</h1>
          <p className="text-white/80 text-sm mt-2 max-w-xl">Só o seu dinheiro: comissões do seu trabalho, suas despesas pessoais e o quanto sobrou no bolso.</p>
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            {[{ k: "month", l: "Este mês" }, { k: "year", l: "Este ano" }, { k: "custom", l: "Período" }].map((p) => (
              <Button key={p.k} size="sm" data-testid={`drv-period-${p.k}`} onClick={() => setPeriod(p.k)}
                className={`${period === p.k ? "bg-white text-slate-900 hover:bg-slate-100" : "bg-white/10 text-white hover:bg-white/20"} border border-white/20`}>
                <Calendar size={14} weight="bold" className="mr-1" /> {p.l}
              </Button>
            ))}
          </div>
          {period === "custom" && (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <Input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="w-40 bg-white/90" />
              <span>até</span>
              <Input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="w-40 bg-white/90" />
              <Button onClick={load} className="bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-bold">Aplicar</Button>
            </div>
          )}
          <div className="mt-4 text-xs text-emerald-200">{data.period.label}</div>
        </div>
      </div>

      <Tabs defaultValue="resumo">
        <TabsList className="h-auto p-1 flex-wrap">
          <TabsTrigger value="resumo" data-testid="drv-tab-resumo" className="py-2 px-4 data-[state=active]:bg-blue-600 data-[state=active]:text-white">Resumo</TabsTrigger>
          <TabsTrigger value="pessoais" data-testid="drv-tab-pessoais" className="py-2 px-4 data-[state=active]:bg-blue-600 data-[state=active]:text-white">Despesas pessoais</TabsTrigger>
          <TabsTrigger value="ir" data-testid="drv-tab-ir" className="py-2 px-4 data-[state=active]:bg-blue-600 data-[state=active]:text-white">Relatório IR</TabsTrigger>
        </TabsList>
        <TabsContent value="pessoais" className="space-y-4"><YearPicker years={years} year={year} setYear={setYear} /><PersonalEntries year={year} /></TabsContent>
        <TabsContent value="ir" className="space-y-4"><YearPicker years={years} year={year} setYear={setYear} /><IRReport year={year} /></TabsContent>
        <TabsContent value="resumo" className="space-y-6">
      {data.role === "autonomo" && <DefaultPctCard value={data.default_commission_pct} onSaved={load} />}
      {/* KPIs principais */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 p-6 shadow-xl text-white" data-testid="drv-commission">
          <div className="text-xs uppercase tracking-widest opacity-80 flex items-center gap-1"><HandCoins size={14} weight="bold" /> Minha comissão</div>
          <div className="text-3xl md:text-4xl font-black mt-2" style={{ fontFamily: "Manrope" }}>{fmt(t.commission_total)}</div>
          <div className="mt-2 text-xs opacity-90">De {t.trips} frete(s){t.bonuses_total ? ` · + ${fmt(t.bonuses_total)} bonificações` : ""}</div>
        </div>
        <div className="rounded-2xl bg-gradient-to-br from-rose-500 to-rose-700 p-6 shadow-xl text-white" data-testid="drv-expenses">
          <div className="text-xs uppercase tracking-widest opacity-80 flex items-center gap-1"><Receipt size={14} weight="bold" /> Minhas despesas</div>
          <div className="text-3xl md:text-4xl font-black mt-2" style={{ fontFamily: "Manrope" }}>{fmt(t.personal_expenses)}</div>
          <div className="mt-2 text-xs opacity-90">Lançadas no período</div>
        </div>
        <div className="rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 p-6 shadow-xl text-slate-900" data-testid="drv-pocket">
          <div className="text-xs uppercase tracking-widest opacity-90 flex items-center gap-1 font-bold"><Wallet size={14} weight="bold" /> Sobrou no bolso</div>
          <div className="text-3xl md:text-4xl font-black mt-2" style={{ fontFamily: "Manrope" }}>{fmt(t.net_pocket)}</div>
          <div className="mt-2 text-xs font-semibold">Comissão + bonificações + outras rendas − despesas pessoais</div>
        </div>
      </div>

      {/* KPIs secundários */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { l: "Viagens", v: fmtNum(t.trips), i: Truck, c: "text-blue-700" },
          { l: "KM rodados", v: `${fmtNum(t.km_total, 0)} km`, i: Truck, c: "text-slate-700" },
          { l: "Bonificações", v: fmt(t.bonuses_total), i: ChartLineUp, c: "text-emerald-700" },
          { l: "Outras rendas", v: fmt(t.other_income), i: CurrencyDollar, c: "text-blue-700" },
        ].map((k) => (
          <Card key={k.l} className="border-2 border-slate-200">
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1"><k.i size={12} weight="bold" /> {k.l}</div>
              <div className={`text-2xl font-black mt-1 ${k.c}`} style={{ fontFamily: "Manrope" }}>{k.v}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Lista de fretes */}
      <Card>
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Truck size={20} weight="duotone" className="text-blue-600" /> Minhas viagens no período</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {data.trips.length === 0 ? <p className="text-sm text-slate-500">Nenhuma viagem no período.</p> : data.trips.map((f) => (
            <div key={f.id} data-testid={`drv-trip-${f.id}`} className="flex items-center gap-3 border rounded-lg p-3 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <div className="font-bold text-slate-900">{f.origin || "?"} → {f.destination || "?"}</div>
                <div className="text-xs text-slate-500">{f.date} · {f.client || "-"}</div>
              </div>
              <div className="text-right">
                <div className="text-lg font-black text-emerald-700" style={{ fontFamily: "Manrope" }}>{fmt(f.commission_value)}</div>
                <div className="text-xs text-slate-500">{fmtNum(f.commission_percent, 1)}%{f.uses_default_pct ? " (padrão)" : ""}{f.bonuses ? ` · + ${fmt(f.bonuses)} bonif.` : ""}</div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function DefaultPctCard({ value, onSaved }) {
  const [v, setV] = useState(value ? String(value).replace(".", ",") : "");
  const save = async () => {
    try { await api.put("/personal/settings", { default_commission_pct: parseNum(v) }); toast.success("Percentual salvo"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  return (
    <Card className="border-2 border-emerald-200" data-testid="drv-default-pct-card">
      <CardContent className="p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <div className="font-semibold text-slate-900">Minha comissão padrão (%)</div>
          <div className="text-xs text-slate-500">Usada nos fretes em que você não lançou o % de comissão. Calculada sobre o valor bruto do frete.</div>
        </div>
        <Input inputMode="decimal" value={v} onChange={(e) => setV(e.target.value)} placeholder="ex: 12" className="w-28" data-testid="drv-default-pct-input" />
        <Button onClick={save} data-testid="btn-save-default-pct" className="bg-emerald-600 hover:bg-emerald-700 text-white">Salvar</Button>
      </CardContent>
    </Card>
  );
}

function YearPicker({ years, year, setYear }) {
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="year-picker">
      <span className="text-sm text-slate-600">Ano:</span>
      {years.map((y) => (
        <Button key={y} size="sm" variant={y === year ? "default" : "outline"} onClick={() => setYear(y)} data-testid={`year-${y}`} className={y === year ? "bg-blue-600 text-white" : ""}>{y}</Button>
      ))}
    </div>
  );
}
